import React, { useState, useEffect } from "react";
import { HashLink } from "./HashLink";
import { NAV_LINKS } from "./data";
import { useLang } from "./LangContext";
import { useAuth } from "./AuthContext";
import { useTenant } from "./TenantContext";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Shield, LogOut } from "lucide-react";

const flagIT = "https://flagcdn.com/w40/it.png";
const flagEN = "https://flagcdn.com/w40/gb.png";
const flagFR = "https://flagcdn.com/w40/fr.png";
const flagES = "https://flagcdn.com/w40/es.png";
const flagDE = "https://flagcdn.com/w40/de.png";

const LANG_OPTIONS = [
    { c: "it", flag: flagIT, label: "Italiano" },
    { c: "en", flag: flagEN, label: "English" },
    { c: "fr", flag: flagFR, label: "Français" },
    { c: "es", flag: flagES, label: "Español" },
    { c: "de", flag: flagDE, label: "Deutsch" },
];

const flagFor = (code) => (LANG_OPTIONS.find((o) => o.c === code)?.flag || flagIT);

export default function Navbar() {
    const { lang, setLang, t } = useLang();
    const { user, logout } = useAuth();
    const { tenant } = useTenant();
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    const goAdmin = () => {
        setOpen(false);
        if (user?.role === "super_admin") nav("/super-admin");
        else if (user) nav("/admin");
        else nav("/login");
    };

    const handleLogout = () => {
        logout();
        setOpen(false);
        nav("/");
    };

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Map nav-link key → tenant.modules key. Keys not in map are always visible.
    const MODULE_MAP = {
        gemello: "digital_twin",
        attrazioni: "attrattori",
        immobili: "immobili",
        itinerari: "itinerari",
        eventi: "eventi",
    };
    const modules = tenant?.modules || {};
    const isModuleActive = (k) => {
        const modKey = MODULE_MAP[k];
        if (!modKey) return true;               // borgo, contatti always visible
        return modules[modKey] !== false;       // default true when field is missing
    };
    const visibleLinks = NAV_LINKS.filter((l) => isModuleActive(l.key));

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-white/85 backdrop-blur-xl border-b border-stone-200 shadow-sm"
                    : "bg-white/60 backdrop-blur-md border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
                <HashLink
                    to="/"
                    className="flex items-center gap-3 group"
                    data-testid="nav-logo"
                >
                    <div className="w-11 h-11 rounded-full overflow-hidden ring-2 ring-emerald-600/20 group-hover:ring-emerald-600/50 transition-all">
                        <img
                            src={tenant?.logo_url || "https://www.visittadasuni.it/wp-content/uploads/2025/11/pietra-logo-tadasuni.jpg"}
                            alt={`${tenant?.name || "VisitTadasuni"} logo`}
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <span className="font-serif text-xl md:text-2xl font-semibold text-emerald-700 tracking-tight">
                        {tenant?.name || "VisitTadasuni"}
                    </span>
                </HashLink>

                <nav className="hidden lg:flex items-center gap-8">
                    {visibleLinks.map((link) => (
                        <HashLink
                            key={link.key}
                            to={link.route}
                            hash={link.href}
                            className="text-sm font-medium text-stone-700 hover:text-emerald-700 transition-colors relative group"
                            data-testid={link.testid}
                        >
                            {link.label[lang] || link.label.en || link.label.it}
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-emerald-600 group-hover:w-full transition-all duration-300"></span>
                        </HashLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <button
                        onClick={goAdmin}
                        className={`hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                            user
                                ? "bg-emerald-600 text-white hover:bg-emerald-700 shadow-md"
                                : "border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                        }`}
                        data-testid="nav-admin"
                        title={user ? `Admin (${user.username})` : "Accesso Admin"}
                    >
                        <Shield size={14} />
                        {user ? "Admin" : (lang === "it" ? "Admin" : "Admin")}
                    </button>
                    {user && (
                        <button
                            onClick={handleLogout}
                            className="hidden md:inline-flex w-9 h-9 items-center justify-center rounded-full border border-stone-300 text-stone-600 hover:border-red-500 hover:text-red-600 transition-colors"
                            data-testid="nav-logout"
                            title="Esci"
                            aria-label="Logout"
                        >
                            <LogOut size={14} />
                        </button>
                    )}

                    <div className="relative">
                        <button
                            onClick={() => setLangOpen((v) => !v)}
                            className="flex items-center gap-2 px-3 py-2 rounded-full border border-stone-200 hover:border-emerald-600/40 bg-white transition-colors"
                            data-testid="lang-switcher"
                            aria-label="Language switcher"
                        >
                            <img
                                src={flagFor(lang)}
                                alt={lang}
                                className="w-5 h-4 object-cover rounded-sm"
                            />
                            <span className="text-sm font-medium text-stone-700 uppercase">{lang}</span>
                            <ChevronDown size={14} className={`transition-transform ${langOpen ? "rotate-180" : ""}`} />
                        </button>
                        {langOpen && (
                            <div
                                className="absolute right-0 mt-2 w-40 bg-white border border-stone-200 rounded-xl shadow-lg overflow-hidden z-50"
                                data-testid="lang-menu"
                            >
                                {LANG_OPTIONS.map((o) => (
                                    <button
                                        key={o.c}
                                        onClick={() => {
                                            setLang(o.c);
                                            setLangOpen(false);
                                        }}
                                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-emerald-50 transition-colors text-left ${
                                            lang === o.c ? "bg-emerald-50/60 text-emerald-700 font-semibold" : "text-stone-700"
                                        }`}
                                        data-testid={`lang-option-${o.c}`}
                                    >
                                        <img src={o.flag} alt={o.c} className="w-5 h-4 object-cover rounded-sm" />
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <button
                        className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full border border-stone-200"
                        onClick={() => setOpen((v) => !v)}
                        data-testid="mobile-menu-toggle"
                        aria-label="Menu"
                    >
                        <div className="space-y-1.5">
                            <span className={`block w-5 h-0.5 bg-stone-700 transition-transform ${open ? "translate-y-2 rotate-45" : ""}`}></span>
                            <span className={`block w-5 h-0.5 bg-stone-700 transition-opacity ${open ? "opacity-0" : ""}`}></span>
                            <span className={`block w-5 h-0.5 bg-stone-700 transition-transform ${open ? "-translate-y-2 -rotate-45" : ""}`}></span>
                        </div>
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden bg-white border-t border-stone-200" data-testid="mobile-menu">
                    <div className="px-6 py-4 flex flex-col gap-2">
                        {visibleLinks.map((link) => (
                            <HashLink
                                key={link.key}
                                to={link.route}
                                hash={link.href}
                                onClick={() => setOpen(false)}
                                className="py-3 px-2 text-stone-800 hover:bg-emerald-50 hover:text-emerald-700 rounded-lg transition-colors"
                                data-testid={`${link.testid}-mobile`}
                            >
                                {link.label[lang] || link.label.en || link.label.it}
                            </HashLink>
                        ))}
                        <button
                            onClick={goAdmin}
                            className={`mt-2 flex items-center gap-2 py-3 px-4 rounded-full font-semibold text-sm transition-colors ${
                                user ? "bg-emerald-600 text-white" : "border-2 border-emerald-600 text-emerald-700"
                            }`}
                            data-testid="nav-admin-mobile"
                        >
                            <Shield size={14} />
                            {user ? `Admin (${user.username})` : "Accesso Admin"}
                        </button>
                        {user && (
                            <button
                                onClick={handleLogout}
                                className="flex items-center gap-2 py-2 px-4 rounded-full text-sm text-red-600 border border-red-200 hover:bg-red-50"
                                data-testid="nav-logout-mobile"
                            >
                                <LogOut size={14} />
                                Esci
                            </button>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
